<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div
      :id="`framework-picker-accordion-${uid}`"
      class="relative w-full rounded-md bg-main-primary py-0.5 pl-0.5 pr-1.5"
      :class="{ 'pb-2': isOpen }"
    >
      <!-- Check mark -->
      <span
        v-if="frameworkIsSelected"
        class="absolute left-[1%] top-[5%] block rounded-full bg-main-primary px-1.5 py-0.5 text-[0.5rem]/[0.5rem] text-white"
      >Selected</span>
      <div
        :id="`framework-picker-accordion-header-${uid}`"
        :aria-controls="`framework-picker-accordion-panel-${uid}`"
        :aria-expanded="isOpen"
        role="button"
        :aria-roledescription="`${esgFramework.short_name} framework accordion`"
        class="w-full cursor-pointer rounded-md bg-main-primary"
        :class="{ 'pb-1.5': isOpen }"
        @click="isOpen = !isOpen"
      >
        <section class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="h-16 w-20 rounded-md bg-white">
              <img
                class="ml-[5%] mt-[5%] h-[90%] w-[90%] object-contain"
                :src="esgFramework.logo"
                :alt="`${esgFramework.short_form} logo`"
              >
            </div>
            <span class="tex-lg font-semibold uppercase text-slate-50">{{
              esgFramework.short_form
            }}</span>
          </div>
          <div class="flex items-center gap-2 text-slate-50">
            <button
              id="toggle-guidance-modal-button"
              type="button"
              class="outline-none"
              @click.stop="$modal.show(`framework-picker-guidance-${uid}`)"
            >
              <LinkIcon custom-style="h-6 w-6" />
            </button>
            <button
              v-if="canOpenFrameworkVideo"
              :id="`framework-picker-video-player-${uid}`"
              type="button"
              class="outline-none"
              @click.stop="openLink(esgFramework.tutorial_video)"
            >
              <PlayIcon custom-style="h-6 w-6" />
            </button>
            <ChevronDownLarge
              custom-style="h-6 w-6"
              class="cursor-pointer transition-all"
              :class="{ 'rotate-180': isOpen }"
            />
          </div>
        </section>
      </div>
      <!-- Hidden panel -->
      <Transition
        enter-class="opacity-0"
        enter-active-class="transition-all duration-300"
        enter-to-class="opacity-100"
        leave-class="opacity-100"
        leave-active-class="transition-all duration-100"
        leave-to-class="opacity-0"
      >
        <section
          v-if="isOpen"
          :id="`framework-picker-accordion-panel-${uid}`"
          class="mt-1 border-t border-gray-300 px-2 pt-2"
        >
          <p class="text-left text-xs uppercase text-slate-50">
            versions
          </p>
          <div class="mt-2 flex flex-col gap-2">
            <div
              v-for="({ id, name }, index) in esgFramework.versions"
              :key="id"
              class="flex items-center gap-1.5 text-sm text-slate-50"
            >
              <input
                :id="`select-framework-version-checkbox-${uid}-${id}`"
                v-model="esgFramework.versions[index].isChecked"
                type="checkbox"
                class="border-1 cursor-pointer rounded-sm border-slate-50 bg-white p-1 text-main-primary focus:ring-transparent disabled:cursor-not-allowed"
                @change="updateCheckedStatus($event, index)"
              >
              <label :for="`select-framework-version-checkbox-${uid}-${id}`">{{
                name
              }}</label>
            </div>
          </div>
        </section>
      </Transition>
    </div>
    <!-- Information Guidance -->
    <ModalComponent
      :name="`framework-picker-guidance-${uid}`"
      class="rounded-full"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="true"
    >
      <section class="border-b p-5">
        <div class="flex items-center justify-between">
          <h3 class="text-lg text-gray-900 lg:text-xl">
            {{ esgFramework.name }} Information
          </h3>
          <button
            type="button"
            class="rounded-md bg-gray-200 p-1 text-gray-400"
            @click="$modal.hide(`framework-picker-guidance-${uid}`)"
          >
            <TimesSmall />
          </button>
        </div>
      </section>
      <section
        class="p-4"
        v-html="esgFramework.information_guidance"
      />
    </ModalComponent>
  </div>
</template>

<script>
export default {
  name: 'EsgFrameworkPickerAccordion',

  props: {
    framework: {
      type: Object,
      required: true,
    },
  },

  emits: [
    'selectionChanged',
    'frameworkVersionSelected',
    'opened',
    'frameworkVersionUnselected',
    'deselectAllLatest',
  ],

  data() {
    return {
      uid: null,
      isOpen: false,
      frameworkIsSelected: false,
      esgFramework: this.framework,
    }
  },

  computed: {
    canOpenFrameworkVideo() {
      return (
        this.esgFramework.tutorial_video
        && this.esgFramework.tutorial_video !== 'null'
      )
    },
  },

  watch: {
    framework(newFramework) {
      this.esgFramework = newFramework
    },
    frameworkIsSelected: {
      immediate: true,
      handler(isSelected) {
        this.$emit('selectionChanged', isSelected)
        if (isSelected) {
          this.$emit(
            'frameworkVersionSelected',
            this.esgFramework.versions.find(({ isChecked }) => isChecked),
          )
        }
      },
    },
    isOpen: {
      immediate: true,
      handler(isAccordionOpen) {
        if (isAccordionOpen) {
          this.$emit('opened')
        }
      },
    },
  },

  mounted() {
    this.uid = this.$.uid
    this.updateCheckedStatus()
  },

  methods: {
    openLink(link) {
      window.open(link, '_blank')
    },
    updateCheckedStatus(event = null, index = null) {
      this.esgFramework.versions = this.esgFramework.versions.map(
        (version, versionIndex) => {
          if (
            versionIndex !== Number(index)
            && event?.target.checked
            && version.isChecked
          ) {
            version.isChecked = false
            this.$emit('frameworkVersionUnselected', version)
          }
          return version
        },
      )
      this.frameworkIsSelected = this.esgFramework.versions.some(
        ({ isChecked }) => isChecked,
      )

      if (event && !event.target.checked && index !== null) {
        this.$emit(
          'frameworkVersionUnselected',
          this.esgFramework.versions[index],
        )
      }
      if (event) {
        this.$emit('deselectAllLatest')
      }
    },
    closePanel() {
      this.isOpen = false
    },
    selectLatest() {
      this.esgFramework.versions = this.esgFramework.versions.map((version, index) => {
        const wasChecked = version.isChecked
        version.isChecked = index === 0

        // Emit events only if the `isChecked` state has changed
        if (wasChecked !== version.isChecked) {
          this.$emit(
            version.isChecked
              ? 'frameworkVersionSelected'
              : 'frameworkVersionUnselected',
            version,
          )
        }

        return version
      })

      this.frameworkIsSelected = true
    },
    deselectLatest() {
      this.esgFramework.versions = this.esgFramework.versions.map(
        (version, index) => {
          if (!index) {
            version.isChecked = false
            this.$emit('frameworkVersionUnselected', version)
          }
          return version
        },
      )
      this.frameworkIsSelected = false
      this.$emit('deselectAllLatest')
    },
  },
}
</script>
